import React, { FC, useMemo } from 'react';
import {
  Badge,
  Box,
  FormControl,
  FormLabel,
  Heading,
  Icon,
  Link,
  Select,
  Stack,
  Switch,
  Text,
} from '@chakra-ui/react';
import {
  Datasources,
  SoccerStages,
  soccerStageSelectOptions,
  type SoccerStageOption,
} from '@adflow/types';
import { LuExternalLink } from 'react-icons/lu';
import { PiSparkle } from 'react-icons/pi';

export type CreatomateDataMappingMagicProps = {
  mappingEnabled: boolean;
  onToggleMapping: (val: boolean) => void;
  suggestedDataSources: Datasources.SuggestedDataSources;
  soccerMomentsEnabled?: boolean;
  inferredSoccerConfig?: Datasources.InferredSoccerMomentsConfig | null;
  selectedStage?: SoccerStages | null;
  onStageOverride?: (stage: SoccerStages) => void;
};

const CreatomateDataMappingMagic: FC<CreatomateDataMappingMagicProps> = ({
  mappingEnabled,
  onToggleMapping,
  suggestedDataSources,
  soccerMomentsEnabled = false,
  inferredSoccerConfig = null,
  selectedStage = null,
  onStageOverride,
}) => {
  const dataSourceNames = useMemo(() => {
    return suggestedDataSources.map((ds) => {
      return ds[0].name;
    });
  }, [suggestedDataSources]);

  return (
    <>
      <Heading size="sm">
        Data Mapping <Icon as={PiSparkle} />
      </Heading>
      {suggestedDataSources.length ? (
        <Text mb="0">
          We matched <Badge colorScheme="blue">{suggestedDataSources.length}</Badge> potential data
          sources for your creative -{' '}
          {dataSourceNames.map((v, idx) => (
            <React.Fragment key={v}>
              <Badge colorScheme="blue" textTransform="none">
                {v}
              </Badge>
              {idx === dataSourceNames.length - 1 ? '' : ', '}
            </React.Fragment>
          ))}
          .
        </Text>
      ) : (
        <>
          <Text mb="0">
            We couldn&apos;t find any potential dynamic elements in your creative. Element names
            should follow a{' '}
            <Link
              color="teal.500"
              href="https://marketing-cloud.sportradar.com/support/solutions/articles/77000515734-sportsbooks-integration-manual"
              isExternal
            >
              naming specification <Icon as={LuExternalLink} />
            </Link>{' '}
            to be detected.
          </Text>
        </>
      )}
      {soccerMomentsEnabled && inferredSoccerConfig && (
        <Box mt={3} p={3} borderWidth={1} borderRadius="md" bg="blackAlpha.50">
          <Heading size="sm" mb={2}>
            Soccer Moments
          </Heading>
          <Stack spacing={2}>
            <Text fontSize="sm">
              Source: <Badge colorScheme="blue">SPORTS_MOMENTS</Badge> Sport:{' '}
              <Badge colorScheme="blue">{inferredSoccerConfig.sport}</Badge>
            </Text>
            <FormControl>
              <FormLabel fontSize="sm" mb={1}>
                Stage
              </FormLabel>
              <Select
                size="sm"
                placeholder={inferredSoccerConfig.stage == null ? 'Select stage…' : undefined}
                value={selectedStage ?? ''}
                onChange={(e) => {
                  const soccerStage = e.target.value as SoccerStages;
                  if (soccerStage && onStageOverride) onStageOverride(soccerStage);
                }}
              >
                {soccerStageSelectOptions.map((opt: SoccerStageOption) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Select>
            </FormControl>
            {inferredSoccerConfig.suggestedMarket && (
              <Text fontSize="sm">
                Market:{' '}
                <Badge colorScheme="green">
                  {Datasources.getFriendlySourceNameFromSourceType(
                    inferredSoccerConfig.suggestedMarket,
                  )}
                </Badge>
              </Text>
            )}
          </Stack>
        </Box>
      )}
      <FormControl display="flex" alignItems="center" mt={2}>
        <FormLabel htmlFor="data-mappings" mb="0">
          Create data mappings?
        </FormLabel>
        <Switch
          id="data-mappings"
          colorScheme="teal"
          isChecked={mappingEnabled}
          disabled={suggestedDataSources.length === 0}
          onChange={(evt) => {
            onToggleMapping(evt.target.checked);
          }}
        />
      </FormControl>
    </>
  );
};

export default CreatomateDataMappingMagic;
